/**
 * /docs — overview / landing page. Frames the two ways to build (in-
 * browser chat next to a preview, or Claude Code), then links into the
 * deeper sections. The "verify your connection" banner lives on Getting
 * started now — the in-browser path doesn't require an OAuth grant,
 * so leading with a connection check confused first-touch users.
 */

import {
  IconBolt,
  IconBot,
  IconGlobe,
  IconInfo,
  IconLaptop,
  IconPlay,
  IconPlug,
  IconRocket,
  IconSparkle,
} from '../../components/Icons.js';
import { ButtonLink, PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { DocCardGrid, DocSection } from './_shared.js';

export function DocsIntro() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs"
        title="Build apps by chatting with an AI"
        description="Describe an app in plain English. An AI writes it, deploys it, hands you a URL."
      />

      <DocsTabs />

      <DocSection title="What is Kin">
        <p>
          Kin hosts small internal tools at Mytra &mdash; lunch sign-ups, wifi lookups, scheduled
          digests, dashboards, forms. You don&apos;t write the code. You tell an AI what you want;
          it writes, deploys to preview, hands you a URL. When the preview is right, you publish to
          live. You manage access, secrets, and version history in this portal.
        </p>
      </DocSection>

      <DocSection title="Two ways to build">
        <p>
          Both run the same backend and produce the same apps. The difference is where you do the
          talking.
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <li>
            <a
              href="/docs/getting-started#in-browser"
              className="group block h-full rounded-xl border border-line bg-surface p-5 hover:border-brand/40 hover:shadow-card transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="h-9 w-9 rounded-lg bg-brand-soft text-brand flex-none flex items-center justify-center group-hover:scale-105 transition-transform">
                  <IconSparkle size={18} />
                </span>
                <h3 className="font-display font-semibold text-base text-ink group-hover:text-brand transition-colors">
                  In the browser
                </h3>
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-brand bg-brand-soft px-2 py-0.5 rounded">
                  Easiest
                </span>
              </div>
              <p className="text-muted text-sm">
                Chat right beside your app preview. Zero install &mdash; if you can open this
                portal, you can build. Best for starting fresh and iterating live.
              </p>
            </a>
          </li>
          <li>
            <a
              href="/docs/getting-started#claude-code"
              className="group block h-full rounded-xl border border-line bg-surface p-5 hover:border-brand/40 hover:shadow-card transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="h-9 w-9 rounded-lg bg-brand-soft text-brand flex-none flex items-center justify-center group-hover:scale-105 transition-transform">
                  <IconLaptop size={18} />
                </span>
                <h3 className="font-display font-semibold text-base text-ink group-hover:text-brand transition-colors">
                  Claude Code
                </h3>
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-muted bg-surface-2 px-2 py-0.5 rounded">
                  Advanced
                </span>
              </div>
              <p className="text-muted text-sm">
                Anthropic&apos;s CLI. Best for file-level control, multi-app flows, scripting, and
                anyone who already lives in a terminal. One-command plugin install.
              </p>
            </a>
          </li>
        </ul>
        <div className="mt-4 flex flex-wrap gap-2">
          <ButtonLink to="/apps/new" variant="primary" size="sm">
            <IconBolt size={13} />
            Create your first app
          </ButtonLink>
          <ButtonLink to="/docs/getting-started" variant="secondary" size="sm">
            Setup walkthrough
          </ButtonLink>
        </div>
      </DocSection>

      <DocSection title="Find your way around">
        <p>
          <strong>Just using apps your teammates built?</strong> Start with{' '}
          <a className="underline" href="/docs/using-apps">
            Using apps
          </a>
          . The rest is for builders.
        </p>
        <DocCardGrid
          cards={[
            {
              to: '/docs/using-apps',
              title: 'Using apps',
              blurb:
                'What the cards on the Apps page mean, how to ask owners for changes, where to find apps not yet shared with you.',
              icon: <IconGlobe size={18} />,
            },
            {
              to: '/docs/getting-started',
              title: 'Getting started',
              blurb:
                'The two paths in detail: the in-browser chat bubble, and installing the Claude Code plugin.',
              icon: <IconRocket size={18} />,
            },
            {
              to: '/docs/first-app',
              title: 'Build your first app',
              blurb: 'Six steps from first prompt to a URL you can share.',
              icon: <IconBolt size={18} />,
            },
            {
              to: '/docs/prompting',
              title: 'Prompting Kin',
              blurb:
                'Problem-shaped prompts, iterate-don’t-restart, never paste secrets, and when the magic word matters.',
              icon: <IconBot size={18} />,
            },
            {
              to: '/docs/examples',
              title: 'Examples',
              blurb:
                'Four prompts that built real apps: a wifi lookup, a lunch sign-up, a Linear bug board, an on-call digest.',
              icon: <IconPlay size={18} />,
            },
            {
              to: '/docs/connections',
              title: 'Private connections',
              blurb:
                'Let an app reach a database tool or internal API running on your own server or VM — without exposing it to the internet.',
              icon: <IconPlug size={18} />,
            },
            {
              to: '/docs/faq',
              title: 'FAQ',
              blurb: 'Secrets, sharing, rollbacks, preview vs live, deletion. Short answers.',
              icon: <IconInfo size={18} />,
            },
          ]}
        />
      </DocSection>

      <DocSection title="Who Kin is for">
        <p>
          Everyone at Mytra. PMs, ops, designers, finance, sales, recruiting, CS. If you&apos;ve
          ever thought{' '}
          <em>&ldquo;a little internal tool would save the team an hour a week&rdquo;</em> but
          didn&apos;t have the engineering budget &mdash; Kin is the budget. Engineers use it for
          prototypes, glue, and the one-off dashboards that don&apos;t belong in the real codebase.
        </p>
        <p>No permission required. No ticket. Open the portal and start.</p>
      </DocSection>
    </div>
  );
}
